import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Save, Edit2, Trash2, X, Eye, Droplets, AlertCircle, RefreshCw, Search, Download } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function DyeingProductionPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [workOrders, setWorkOrders] = useState([]);
  const [selectedWO, setSelectedWO] = useState(null);
  const [productionHistory, setProductionHistory] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [formData, setFormData] = useState({
    wo_id: "",
    date: new Date().toISOString().split('T')[0],
    size: "",
    design_name: "",
    pieces_produced: "",
    weight_produced: ""
  });
  const [editingEntry, setEditingEntry] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const formRef = useRef(null);

  const canAddProduction = currentUser?.role === 'admin' || currentUser?.department?.toLowerCase() === 'dyeing';

  useEffect(() => {
    fetchWorkOrders();
    fetchCurrentUser();
  }, []);

  useEffect(() => {
    const woId = searchParams.get('wo');
    if (woId && workOrders.length > 0) {
      const wo = workOrders.find(w => w.id === woId);
      if (wo) handleWOSelect(woId);
    }
  }, [searchParams, workOrders]);

  const fetchCurrentUser = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCurrentUser(response.data);
    } catch (err) {
      console.error("Failed to fetch user:", err);
    }
  };

  const fetchWorkOrders = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/work-orders-dyeing`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWorkOrders(response.data);
    } catch (err) {
      console.error("Failed to fetch work orders:", err);
    }
  };

  const handleWOSelect = async (woId) => {
    const wo = workOrders.find(w => w.id === woId);
    setSelectedWO(wo);
    setFormData({ ...formData, wo_id: woId, size: "", design_name: "" });
    setEditingEntry(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/dyeing-production/${woId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProductionHistory(response.data);
    } catch (err) {
      console.error("Failed to fetch production history:", err);
      setProductionHistory(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const token = localStorage.getItem('token');
      
      if (editingEntry) {
        await axios.put(`${API}/dyeing-production/${editingEntry.id}`, {
          date: formData.date,
          size: formData.size,
          design_name: formData.design_name,
          pieces_produced: parseInt(formData.pieces_produced),
          weight_produced: parseFloat(formData.weight_produced)
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSuccess("Production entry updated successfully!");
        setEditingEntry(null);
      } else {
        await axios.post(`${API}/dyeing-production`, {
          ...formData,
          design_name: formData.design_name
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSuccess("Production entry added successfully!");
      }
      
      setFormData({ ...formData, size: "", design_name: "", pieces_produced: "", weight_produced: "" });
      
      if (selectedWO) {
        const historyRes = await axios.get(`${API}/dyeing-production/${selectedWO.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setProductionHistory(historyRes.data);
      }
      
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      console.error("Failed to save production:", err);
      setError(err.response?.data?.detail || "Failed to save production entry");
    } finally {
      setLoading(false);
    }
  };

  const handleEditEntry = (entry) => {
    setEditingEntry(entry);
    setFormData({
      ...formData,
      date: entry.date,
      size: entry.size || "",
      design_name: entry.design_name || "",
      pieces_produced: entry.pieces_produced.toString(),
      weight_produced: entry.weight_produced.toString()
    });
    // Scroll to form after a short delay to ensure state is updated
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const handleCancelEdit = () => {
    setEditingEntry(null);
    setFormData({
      ...formData,
      date: new Date().toISOString().split('T')[0],
      size: "", design_name: "", pieces_produced: "", weight_produced: ""
    });
  };

  const handleDeleteEntry = async (entryId) => {
    if (!window.confirm("Are you sure you want to delete this production entry?")) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/dyeing-production/${entryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSuccess("Production entry deleted successfully!");
      
      if (selectedWO) {
        const historyRes = await axios.get(`${API}/dyeing-production/${selectedWO.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setProductionHistory(historyRes.data);
      }
      
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      console.error("Failed to delete entry:", err);
      setError(err.response?.data?.detail || "Failed to delete production entry");
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Droplets className="w-6 h-6 text-blue-500" />
            Dyeing Production
          </h1>
          <p className="text-muted-foreground text-sm mt-1">Daily production tracking for dyeing department</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchWorkOrders}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Work Order Sidebar */}
        <Card className="lg:col-span-1">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Ready for Dyeing</CardTitle>
            <CardDescription>Work orders with weaving complete</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Search Bar */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search WO, customer..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-10"
                data-testid="wo-search"
              />
            </div>
            
            <div className="space-y-2 max-h-[400px] sm:max-h-[500px] overflow-y-auto">
              {workOrders.length === 0 ? (
                <div className="text-center py-8">
                  <Droplets className="w-10 h-10 mx-auto mb-3 text-muted-foreground/30" />
                  <p className="text-sm text-muted-foreground">No work orders ready for dyeing</p>
                </div>
              ) : (
                workOrders
                  .filter(wo => 
                    wo.wo_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    wo.customer_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    wo.customer_po_number?.toLowerCase().includes(searchQuery.toLowerCase())
                  )
                  .map(wo => (
                  <div
                    key={wo.id}
                    onClick={() => handleWOSelect(wo.id)}
                    className={`p-3 rounded-lg border cursor-pointer transition-all ${
                      selectedWO?.id === wo.id
                        ? 'bg-blue-500 text-white border-blue-500'
                        : 'hover:bg-muted border-border'
                    }`}
                    data-testid={`wo-select-${wo.id}`}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-semibold text-sm">{wo.wo_number}</div>
                        <div className="text-xs opacity-80">{wo.customer_name}</div>
                      </div>
                      <Badge 
                        variant={wo.current_stage === 'dyeing' ? 'default' : 'secondary'} 
                        className={`text-xs ${
                          wo.current_stage === 'shipped' ? 'bg-green-600 text-white' : 
                          wo.current_stage === 'ready' ? 'bg-amber-500 text-white' : 
                          ''
                        }`}
                      >
                        {wo.current_stage === 'shipped' ? 'Shipped' : 
                         wo.current_stage === 'ready' ? 'Ready' : 
                         wo.current_stage}
                      </Badge>
                    </div>
                    <div className="text-xs opacity-70 mt-2">
                      {wo.total_qty_pcs} pcs | {wo.total_qty_weight} KG
                    </div>
                  </div>
                ))
              )}
              {workOrders.length > 0 && workOrders.filter(wo => 
                wo.wo_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                wo.customer_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                wo.customer_po_number?.toLowerCase().includes(searchQuery.toLowerCase())
              ).length === 0 && (
                <div className="text-center py-4 text-sm text-muted-foreground">
                  No matching work orders
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Production Entry Form */}
        <div className="lg:col-span-2 space-y-6">
          <Card ref={formRef}>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                {selectedWO ? `Dyeing Entry - ${selectedWO.wo_number}` : 'Dyeing Entry'}
                {selectedWO && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="ml-auto h-8 text-green-600 border-green-300 hover:bg-green-50"
                    onClick={async () => {
                      try {
                        const token = localStorage.getItem('token');
                        const resp = await axios.get(`${API}/export/work-order/${selectedWO.id}/document/excel`, {
                          headers: { Authorization: `Bearer ${token}` },
                          responseType: 'blob'
                        });
                        const url = window.URL.createObjectURL(new Blob([resp.data]));
                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', `${selectedWO.wo_number}.xlsx`);
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                      } catch (err) {
                        alert('Failed to download');
                      }
                    }}
                    data-testid="download-wo-excel"
                  >
                    <Download className="w-4 h-4 mr-1" />
                    Download WO
                  </Button>
                )}
              </CardTitle>
              {selectedWO && (
                <CardDescription>
                  {selectedWO.customer_name} | PO: {selectedWO.customer_po_number}
                      {selectedWO.current_stage === 'shipped' && (
                        <span className="ml-2 text-red-600 font-semibold">( Shipped - No entries allowed )</span>
                      )}
                </CardDescription>
              )}
            </CardHeader>
            <CardContent>
              {!selectedWO ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Droplets className="w-12 h-12 mx-auto mb-3 opacity-20" />
                  <p>Select a Work Order to add dyeing production</p>
                </div>
              ) : !canAddProduction ? (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-center gap-3">
                  <AlertCircle className="text-yellow-600 w-5 h-5" />
                  <span className="text-yellow-800 font-medium">Only Dyeing department can add production entries</span>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  {selectedWO.size_items?.length > 0 && (
                    <div className="bg-blue-50 rounded-lg p-3 text-sm">
                      <div className="font-medium mb-2">Order Sizes:</div>
                      <div className="flex flex-wrap gap-2">
                        {selectedWO.size_items.map((item, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs bg-white">
                            {item.size} - {item.quality} ({item.qty_pcs} pcs)
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Date *</Label>
                      <Input
                        type="date"
                        value={formData.date}
                        onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Size & Design *</Label>
                      {selectedWO.size_items?.length > 0 ? (
                        <Select
                          value={formData.size && formData.design_name ? `${formData.size}|||${formData.design_name}` : ""}
                          onValueChange={(value) => {
                            // Value format: "size|||design" to handle duplicates
                            const [size, design] = value.split('|||');
                            setFormData({ 
                              ...formData, 
                              size: size,
                              design_name: design
                            });
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select size & design" />
                          </SelectTrigger>
                          <SelectContent>
                            {selectedWO.size_items.map((item, idx) => (
                              <SelectItem key={idx} value={`${item.size}|||${item.quality}`}>
                                {item.size} - {item.quality} ({item.gsm} GSM)
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : selectedWO.order_items?.length > 0 ? (
                        <Select
                          value={formData.size && formData.design_name ? `${formData.size}|||${formData.design_name}` : ""}
                          onValueChange={(value) => {
                            const [size, design] = value.split('|||');
                            setFormData({ 
                              ...formData, 
                              size: size,
                              design_name: design
                            });
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select size & design" />
                          </SelectTrigger>
                          <SelectContent>
                            {selectedWO.order_items.map((item, idx) => {
                              const size = item.size_cm || item.size || '';
                              const design = item.design_name || item.quality || '';
                              return (
                                <SelectItem key={idx} value={`${size}|||${design}`}>
                                  {size} - {design} ({item.gsm} GSM)
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input
                          value={formData.size}
                          onChange={(e) => setFormData({ ...formData, size: e.target.value })}
                          placeholder="Enter size"
                          required
                        />
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Pieces Produced *</Label>
                      <Input
                        type="number"
                        value={formData.pieces_produced}
                        onChange={(e) => setFormData({ ...formData, pieces_produced: e.target.value })}
                        placeholder="Enter pieces"
                        required
                        min="1"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Weight (KG) *</Label>
                      <Input
                        type="number"
                        value={formData.weight_produced}
                        onChange={(e) => setFormData({ ...formData, weight_produced: e.target.value })}
                        placeholder="Enter weight"
                        required
                        min="0.01"
                        step="0.01"
                      />
                    </div>
                  </div>

                  {error && (
                    <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-sm text-destructive">
                      {error}
                    </div>
                  )}
                  {success && (
                    <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
                      ✅ {success}
                    </div>
                  )}

                  <div className="flex gap-3">
                    {editingEntry && (
                      <Button type="button" variant="outline" onClick={handleCancelEdit} className="flex-1">
                        <X className="w-4 h-4 mr-2" />
                        Cancel
                      </Button>
                    )}
                    <Button type="submit" disabled={loading || selectedWO?.current_stage === 'shipped'} className={`${editingEntry ? 'flex-1' : 'w-full'} bg-blue-500 hover:bg-blue-600`} data-testid="submit-production">
                      <Save className="w-4 h-4 mr-2" />
                      {loading ? "Saving..." : editingEntry ? "Update Entry" : "Save Entry"}
                    </Button>
                  </div>
                </form>
              )}
            </CardContent>
          </Card>

          {/* Production History */}
          {selectedWO && productionHistory && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Eye className="w-5 h-5" />
                  Dyeing Status - {selectedWO.wo_number}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Order Size-wise Summary */}
                <div className="bg-slate-50 border rounded-lg p-4">
                  <h4 className="font-semibold text-sm mb-3 text-slate-700">📦 Order Sizes (Total Qty)</h4>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-100">
                          <TableHead className="text-xs">Size</TableHead>
                          <TableHead className="text-xs">Quality</TableHead>
                          <TableHead className="text-xs">GSM</TableHead>
                          <TableHead className="text-xs text-right">Order Qty (Pcs)</TableHead>
                          <TableHead className="text-xs text-right">Order Qty (KG)</TableHead>
                          <TableHead className="text-xs text-right bg-blue-50">Plan Pcs</TableHead>
                          <TableHead className="text-xs text-right bg-blue-50">Plan Kgs</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(selectedWO.order_items?.length > 0 ? selectedWO.order_items : selectedWO.size_items)?.map((item, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="font-medium">{item.size_cm || item.size}</TableCell>
                            <TableCell>{item.design_name || item.quality}</TableCell>
                            <TableCell>{item.gsm}</TableCell>
                            <TableCell className="text-right font-semibold">{item.order_pcs || item.qty_pcs} pcs</TableCell>
                            <TableCell className="text-right">{item.order_kgs || item.qty_weight || '-'} KG</TableCell>
                            <TableCell className="text-right font-semibold bg-blue-50">{item.plan_pcs || item.order_pcs || item.qty_pcs || '-'} pcs</TableCell>
                            <TableCell className="text-right bg-blue-50">{item.plan_kgs || '-'} KG</TableCell>
                          </TableRow>
                        ))}
                        <TableRow className="bg-slate-100 font-bold">
                          <TableCell colSpan={3}>Total Order</TableCell>
                          <TableCell className="text-right">{selectedWO.total_qty_pcs} pcs</TableCell>
                          <TableCell className="text-right">{selectedWO.total_qty_weight || 0} KG</TableCell>
                          <TableCell className="text-right bg-blue-50">
                            {(selectedWO.order_items?.reduce((sum, i) => sum + (parseInt(i.plan_pcs) || 0), 0) || selectedWO.total_qty_pcs)} pcs
                          </TableCell>
                          <TableCell className="text-right bg-blue-50">
                            {(selectedWO.order_items?.reduce((sum, i) => sum + (parseFloat(i.plan_kgs) || 0), 0) || 0).toFixed(2)} KG
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                </div>

                {/* Weaving Production Summary (What came from Weaving) */}
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <h4 className="font-semibold text-sm mb-3 text-purple-700">🧵 From Weaving (Size-wise Weaved Pcs)</h4>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-purple-100">
                          <TableHead className="text-xs">Size</TableHead>
                          <TableHead className="text-xs text-right">Weaved Pcs</TableHead>
                          <TableHead className="text-xs text-right">Weight (KG)</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {productionHistory.size_wise_weaving?.length > 0 ? (
                          productionHistory.size_wise_weaving.map((item, idx) => (
                            <TableRow key={idx}>
                              <TableCell className="font-medium">{item.size}</TableCell>
                              <TableCell className="text-right">{item.pcs} pcs</TableCell>
                              <TableCell className="text-right">{parseFloat(item.kg || 0).toFixed(2)} KG</TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={3} className="text-center text-muted-foreground">No weaving data yet</TableCell>
                          </TableRow>
                        )}
                        <TableRow className="bg-purple-100 font-bold">
                          <TableCell>Total from Weaving</TableCell>
                          <TableCell className="text-right">{productionHistory.from_weaving_pcs} pcs</TableCell>
                          <TableCell className="text-right">{productionHistory.from_weaving_kg} KG</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                </div>

                {/* Dyeing Progress Stats */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-semibold text-sm mb-3 text-blue-700">💧 Dyeing Progress</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                    <div className="bg-white p-3 rounded-lg text-center border">
                      <div className="text-2xl font-bold text-slate-600">{productionHistory.total_pieces}</div>
                      <div className="text-xs text-muted-foreground">Plan Qty (Pcs)</div>
                    </div>
                    <div className="bg-white p-3 rounded-lg text-center border">
                      <div className="text-2xl font-bold text-purple-600">{productionHistory.from_weaving_pcs}</div>
                      <div className="text-xs text-muted-foreground">From Weaving (Pcs)</div>
                    </div>
                    <div className="bg-white p-3 rounded-lg text-center border">
                      <div className="text-2xl font-bold text-blue-600">{productionHistory.produced_pieces}</div>
                      <div className="text-xs text-muted-foreground">Dyed (Pcs)</div>
                    </div>
                    <div className="bg-white p-3 rounded-lg text-center border">
                      <div className="text-2xl font-bold text-orange-600">{productionHistory.balance_pieces}</div>
                      <div className="text-xs text-muted-foreground">Balance (Pcs)</div>
                    </div>
                  </div>
                  
                  {/* Size-wise Production Summary */}
                  {productionHistory.size_wise_data?.length > 0 && (
                    <div className="mt-4 mb-4">
                      <h5 className="font-medium text-sm mb-2 text-blue-700">Size-wise Production</h5>
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-blue-100">
                              <TableHead className="text-xs">Size</TableHead>
                              <TableHead className="text-xs">Quality</TableHead>
                              <TableHead className="text-xs text-right">Plan Pcs</TableHead>
                              <TableHead className="text-xs text-right">Produced</TableHead>
                              <TableHead className="text-xs text-right">Balance</TableHead>
                              <TableHead className="text-xs text-right">%</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {productionHistory.size_wise_data.map((item, idx) => (
                              <TableRow key={idx}>
                                <TableCell className="font-medium">{item.size}</TableCell>
                                <TableCell>{item.quality}</TableCell>
                                <TableCell className="text-right">{item.plan_pcs}</TableCell>
                                <TableCell className="text-right text-blue-600 font-semibold">{item.produced_pcs}</TableCell>
                                <TableCell className="text-right text-orange-600">{item.balance_pcs}</TableCell>
                                <TableCell className="text-right">
                                  <Badge variant={item.completion >= 100 ? "default" : item.completion >= 50 ? "secondary" : "outline"}>
                                    {item.completion}%
                                  </Badge>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  )}
                  
                  <div className="flex items-center gap-2 mb-2">
                    <Progress value={Math.min(productionHistory.completion_percentage, 100)} className="h-2 flex-1" />
                    <span className="text-sm font-semibold text-green-600">{productionHistory.completion_percentage}%</span>
                  </div>
                </div>

                {/* Entries Table */}
                {productionHistory.entries?.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Size</TableHead>
                        <TableHead>Design</TableHead>
                        <TableHead className="text-right">Pieces</TableHead>
                        <TableHead className="text-right">Weight</TableHead>
                        <TableHead>By</TableHead>
                        <TableHead className="text-center">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {productionHistory.entries.map((entry, idx) => (
                        <TableRow key={entry.id || idx}>
                          <TableCell className="text-sm">{new Date(entry.date).toLocaleDateString()}</TableCell>
                          <TableCell className="text-sm">{entry.size}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">{entry.design_name || '-'}</TableCell>
                          <TableCell className="text-right font-semibold">{entry.pieces_produced}</TableCell>
                          <TableCell className="text-right font-semibold">{entry.weight_produced} KG</TableCell>
                          <TableCell className="text-xs text-muted-foreground">{entry.created_by}</TableCell>
                          <TableCell className="text-center">
                            {canAddProduction && (
                              <div className="flex justify-center gap-1">
                                <Button variant="outline" size="sm" className="min-h-[44px] px-3" onClick={() => handleEditEntry(entry)}>
                                  <Edit2 className="w-4 h-4" />
                                </Button>
                                <Button variant="outline" size="sm" className="min-h-[44px] px-3 text-destructive" onClick={() => handleDeleteEntry(entry.id)}>
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-6 text-muted-foreground">
                    No dyeing entries yet
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
